import React from 'react';
import { X, AlertTriangle, Wifi, WifiOff, Smartphone, Loader2, AlertCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { useWhatsAppStatus, type ConnectionStatus } from '../hooks/useWhatsAppStatus.ts';

interface WhatsAppStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: ConnectionStatus;
  onConnect: () => void;
  isMaintenanceMode?: boolean; // 🆕 Indica se está em modo manutenção
}

const WhatsAppStatusModal: React.FC<WhatsAppStatusModalProps> = ({
  isOpen,
  onClose,
  status,
  onConnect,
  isMaintenanceMode = false
}) => {
  const { isDark } = useTheme();

  // Lembrar opção por tempo延长ido se estiver em modo manutenção
  const handleRemindLater = () => {
    // 🆕 Se estiver em modo manutenção, lembrar por 4 horas em vez de 1
    const hours = isMaintenanceMode ? 4 : 1;
    const remindUntil = Date.now() + (hours * 60 * 60 * 1000);
    localStorage.setItem('whatsapp-remind-later', remindUntil.toString());

    // 🆕 Salvar timestamp especial para detectar modo manutenção
    if (isMaintenanceMode) {
      localStorage.setItem('whatsapp-maintenance-remind', Date.now().toString());
    }

    onClose();
  };

  // Verificar se deve mostrar popup (com base na opção "Lembrar depois")
  const shouldShowModal = () => {
    const remindLater = localStorage.getItem('whatsapp-remind-later');
    if (!remindLater) return true;

    const remindUntil = parseInt(remindLater);
    return Date.now() > remindUntil;
  };

  if (!isOpen) return null;

  const getStatusContent = () => {
    switch (status) {
      case 'connected':
        return {
          icon: <Wifi className="w-6 h-6 text-green-500" />,
          title: 'WhatsApp Conectado',
          message: 'Sua conexão com WhatsApp está ativa e funcionando perfeitamente.',
          bgColor: isDark ? 'bg-green-900/20' : 'bg-green-50',
          borderColor: isDark ? 'border-green-700' : 'border-green-200',
          titleColor: isDark ? 'text-green-300' : 'text-green-800',
          textColor: isDark ? 'text-green-200' : 'text-green-700',
          buttonText: 'Fechar',
          buttonColor: 'bg-green-600 hover:bg-green-700'
        };

      case 'disconnected':
        return {
          icon: <WifiOff className="w-6 h-6 text-red-500" />,
          title: 'WhatsApp Desconectado',
          message: 'Sua conexão com WhatsApp está desativada. Para continuar enviando mensagens, configure sua conexão agora.',
          bgColor: isDark ? 'bg-red-900/20' : 'bg-red-50',
          borderColor: isDark ? 'border-red-700' : 'border-red-200',
          titleColor: isDark ? 'text-red-300' : 'text-red-800',
          textColor: isDark ? 'text-red-200' : 'text-red-700',
          buttonText: 'Configurar Agora',
          buttonColor: 'bg-red-600 hover:bg-red-700'
        };

      case 'connecting':
        return {
          icon: <Loader2 className="w-6 h-6 text-yellow-500 animate-spin" />,
          title: 'Conectando WhatsApp',
          message: 'Estamos estabelecendo a conexão com o WhatsApp. Isso pode levar alguns instantes...',
          bgColor: isDark ? 'bg-yellow-900/20' : 'bg-yellow-50',
          borderColor: isDark ? 'border-yellow-700' : 'border-yellow-200',
          titleColor: isDark ? 'text-yellow-300' : 'text-yellow-800',
          textColor: isDark ? 'text-yellow-200' : 'text-yellow-700',
          buttonText: 'Aguarde...',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700'
        };

      case 'qr':
        return {
          icon: <Smartphone className="w-6 h-6 text-blue-500" />,
          title: 'QR Code Disponível',
          message: 'Escaneie o QR Code com seu WhatsApp para completar a conexão.',
          bgColor: isDark ? 'bg-blue-900/20' : 'bg-blue-50',
          borderColor: isDark ? 'border-blue-700' : 'border-blue-200',
          titleColor: isDark ? 'text-blue-300' : 'text-blue-800',
          textColor: isDark ? 'text-blue-200' : 'text-blue-700',
          buttonText: 'Ver QR Code',
          buttonColor: 'bg-blue-600 hover:bg-blue-700'
        };

      default:
        return {
          icon: <AlertCircle className="w-6 h-6 text-gray-500" />,
          title: 'Status Indisponível',
          message: 'Não foi possível verificar o status da conexão com WhatsApp.',
          bgColor: isDark ? 'bg-gray-900/20' : 'bg-gray-50',
          borderColor: isDark ? 'border-gray-700' : 'border-gray-200',
          titleColor: isDark ? 'text-gray-300' : 'text-gray-800',
          textColor: isDark ? 'text-gray-200' : 'text-gray-700',
          buttonText: 'Fechar',
          buttonColor: 'bg-gray-600 hover:bg-gray-700'
        };
    }
  };

  const content = getStatusContent();

  const handleButtonClick = () => {
    if (status === 'disconnected') {
      onConnect();
    } else if (status === 'qr') {
      onConnect();
    } else if (status === 'connected' || status === null) {
      onClose();
    }
    // Para 'connecting', não faz nada (o botão fica desativado)
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
      <div className={`rounded-2xl shadow-2xl max-w-md w-full border ${content.borderColor} ${content.bgColor}`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${content.bgColor}`}>
                {content.icon}
              </div>
              <div>
                <h3 className={`text-xl font-semibold ${content.titleColor}`}>
                  {content.title}
                </h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Message */}
          <div className="mb-8">
            <p className={`text-sm leading-relaxed ${content.textColor}`}>
              {content.message}
            </p>
          </div>

          {/* Additional info for disconnected status */}
          {status === 'disconnected' && (
            <div className={`mb-6 p-4 rounded-lg border ${content.borderColor} ${content.bgColor}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className={`font-medium mb-1 ${content.titleColor}`}>
                    Importante:
                  </p>
                  <p className={content.textColor}>
                    Sem uma conexão ativa, você não poderá enviar ou receber mensagens de WhatsApp.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {status !== 'connecting' && (
              <>
                <button
                  onClick={handleButtonClick}
                  className={`flex-1 px-4 py-3 text-white rounded-lg font-semibold transition-colors duration-200 ${content.buttonColor}`}
                >
                  {content.buttonText}
                </button>
                {status === 'disconnected' && (
                  <button
                    onClick={handleRemindLater}
                    className={`px-4 py-3 rounded-lg font-medium border transition-colors duration-200 ${
                      isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Lembrar Depois
                  </button>
                )}
              </>
            )}

            {status === 'connecting' && (
              <button
                disabled
                className={`w-full px-4 py-3 rounded-lg font-medium border cursor-not-allowed ${
                  isDark
                    ? 'border-gray-600 text-gray-400 bg-gray-700'
                    : 'border-gray-300 text-gray-500 bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {content.buttonText}
                </div>
              </button>
            )}
          </div>

          {/* Footer info for connected status */}
          {status === 'connected' && (
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <p className={`text-xs ${content.textColor}`}>
                  Status verificado em tempo real
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppStatusModal;